// Contabilidad: empleados del estudio, sus documentos laborales y el cálculo
// de la liquidación. Ver backend/src/routes/contabilidad.ts.
import { api } from './api';

export type Empleado = {
  id: string;
  nombre: string;
  /** El usuario de Tabularis que es este empleado, si tiene cuenta. */
  usuarioId: string | null;
  rut: string | null;
  cargo: string | null;
  fechaIngreso: string | null;
  tipoContrato: 'indefinido' | 'plazo_fijo' | 'obra_faena';
  terminoContrato: string | null;
  afp: string | null;
  salud: string | null;
  planIsapreUF: number | null;
  sueldoBase: number | null;
  jornadaHoras: number | null;
  gratificacion: 'art50' | 'art47' | 'ninguna';
  activo: boolean;
  documentos?: number;
};

export type ClaseDocumento = 'LIQUIDACION' | 'CONTRATO' | 'ANEXO' | 'FINIQUITO' | 'OTRO';
export const CLASES: { id: ClaseDocumento; titulo: string; singular: string }[] = [
  { id: 'LIQUIDACION', titulo: 'Liquidaciones', singular: 'Liquidación' },
  { id: 'CONTRATO', titulo: 'Contratos', singular: 'Contrato' },
  { id: 'ANEXO', titulo: 'Anexos', singular: 'Anexo' },
  { id: 'FINIQUITO', titulo: 'Finiquitos', singular: 'Finiquito' },
  { id: 'OTRO', titulo: 'Otros', singular: 'Otro documento' },
];

export type DocumentoLaboral = {
  id: string;
  empleadoId: string;
  clase: ClaseDocumento;
  periodo: string | null;
  fecha: string | null;
  liquido: number | null;
  nota: string | null;
  nombre: string;
  size: number | null;
  subidoPor: string | null;
  creado: string;
};

export type Linea = { nombre: string; detalle?: string; monto: number };
export type Calculo = {
  resultado: {
    haberes: Linea[]; noImponibles: Linea[]; descuentos: Linea[]; aportes: Linea[];
    imponible: number; tributable: number; totalHaberes: number; totalDescuentos: number;
    liquido: number; costoEmpresa: number; observaciones: string[];
  };
  indicadores: { uf: number; ufFecha: string; utm: number; fuente: string; verificado: boolean };
  contadora: { id: string; nombre: string; liquido: number } | null;
};

export type EntradaHistorial = {
  cuando: string;
  quien: string;
  accion: string;
  empleado: string;
  documento: { nombre: string; clase: ClaseDocumento; periodo: string | null } | null;
};

export const AFPS = ['Capital', 'Cuprum', 'Habitat', 'Modelo', 'PlanVital', 'ProVida', 'Uno'];

export const listarEmpleados = () => api<Empleado[]>('/contabilidad/empleados');
export const crearEmpleado = (e: Partial<Empleado>) => api<Empleado>('/contabilidad/empleados', { method: 'POST', body: e });
export const editarEmpleado = (id: string, e: Partial<Empleado>) => api<Empleado>(`/contabilidad/empleados/${id}`, { method: 'PATCH', body: e });
export const listarDocumentos = (empleadoId: string) => api<DocumentoLaboral[]>(`/contabilidad/empleados/${empleadoId}/documentos`);
export const borrarDocumento = (id: string) => api(`/contabilidad/documentos/${id}`, { method: 'DELETE' });
export const urlDocumento = (id: string, descargar = false) =>
  api<{ url: string }>(`/contabilidad/documentos/${id}/url${descargar ? '?descargar=1' : ''}`).then((r) => r.url);
export const historial = (empleadoId?: string) =>
  api<EntradaHistorial[]>(`/contabilidad/historial${empleadoId ? `?empleadoId=${encodeURIComponent(empleadoId)}` : ''}`);
/** Lo del mes: lo que no se manda sale de la ficha del empleado. */
export type AjustesMes = {
  diasTrabajados?: number; horasExtra?: number; bonosImponibles?: number; noImponibles?: number;
  anticipos?: number; otrosDescuentos?: number;
  sueldoBase?: number | null; jornadaHoras?: number | null; tipoContrato?: Empleado['tipoContrato'] | null;
};
export const calcular = (empleadoId: string, entrada: AjustesMes & { periodo: string }) =>
  api<Calculo>(`/contabilidad/empleados/${empleadoId}/calcular`, { method: 'POST', body: entrada });

export type Previred = {
  empleados: { empleadoId: string; nombre: string; imponible: number; liquido: number; costoEmpresa: number; observaciones: string[] }[];
  distribucion: { grupo: string; nombre: string; monto: number }[];
  total: number;
  indicadores: Calculo['indicadores'];
};
export const previred = (periodo: string, filas: (AjustesMes & { empleadoId: string })[]) =>
  api<Previred>('/contabilidad/previred', { method: 'POST', body: { periodo, filas } });

/** Días que corresponden en el mes según el ingreso y el término del
 *  contrato (meses de 30 días, como la liquidación). */
export function diasDelMes(e: Empleado, periodo: string): number {
  let desde = 1;
  let hasta = 30;
  if (e.fechaIngreso?.startsWith(periodo)) desde = Number(e.fechaIngreso.slice(8, 10));
  if (e.terminoContrato?.startsWith(periodo)) hasta = Math.min(30, Number(e.terminoContrato.slice(8, 10)));
  return Math.max(0, hasta - desde + 1);
}

/** ¿Trabajaba en el estudio ese mes? */
export function trabajabaEn(e: Empleado, periodo: string): boolean {
  if (e.fechaIngreso && e.fechaIngreso.slice(0, 7) > periodo) return false;
  if (e.terminoContrato && e.terminoContrato.slice(0, 7) < periodo) return false;
  return true;
}

/** Sube un documento: el servidor da el enlace, el navegador sube directo a
 *  S3 y después se confirma. Si la subida falla, la ficha queda sin confirmar
 *  y no se muestra. */
export async function subirDocumento(
  empleadoId: string,
  file: File,
  datos: { clase: ClaseDocumento; periodo?: string | null; fecha?: string | null; liquido?: number | null; nota?: string | null },
) {
  const contentType = file.type || 'application/octet-stream';
  const res = await api<{ documento: DocumentoLaboral; uploadUrl: string }>(`/contabilidad/empleados/${empleadoId}/documentos`, {
    method: 'POST',
    body: { ...datos, nombre: file.name, contentType, size: file.size },
  });
  let put: Response;
  try {
    put = await fetch(res.uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: file });
  } catch (e: any) {
    throw new Error(`No se pudo enviar el archivo a destino (${e?.message || 'error de red'}).`);
  }
  if (!put.ok) throw new Error(`El almacenamiento rechazó el archivo (${put.status}).`);
  await api(`/contabilidad/documentos/${res.documento.id}/confirmar`, { method: 'POST' });
  return res.documento;
}
